/**
 * src/pages/landing/PricingGrid.tsx
 *
 * Pricing tier grid for LandingPage (Phase 38 — pricing & landing content).
 * Renders the whole truth: Trial (14 days of Pro, free, honest about day 15),
 * Pro (the one buyable tier — plan 38-01's tracer slice), and Team (announced
 * only, no CTA, zero interactive elements — plan 38-03).
 *
 * Mirrors BenefitList.tsx's shape: FC, no props, no local state, useTranslation.
 * Every price string is an i18n lookup — billing.price_monthly / billing.price_annual
 * for Pro (the same keys PaywallPage.tsx and UpgradePromptModal.tsx read) and
 * landing.pricing.team_price for Team's landing-only indicative figure — so this
 * component never computes or hardcodes a price of its own.
 *
 * DOM order is fixed Trial -> Pro -> Team at every breakpoint (no order-* utility
 * is used anywhere in this file), matching the honest chronological order a real
 * user experiences: trial first, upgrade to Pro, Team is a future announcement.
 *
 * The Team card is the phase's central honesty claim: it contains zero <button>,
 * <a>, or other focusable elements — proven fail-first by
 * test/pages/landing/PricingGrid.test.tsx ("the Team card has no button, link or
 * other focusable element").
 */

import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Zap, Users, CheckCircle2 } from 'lucide-react';
import { SERVICES_DB } from '@/entities/service/data/initialData';

const CARD_BASE = 'rounded-3xl border shadow-card p-6 flex flex-col gap-3 bg-white dark:bg-slate-900';

export const PricingGrid: FC = () => {
  const { t } = useTranslation();

  return (
    <section className="flex flex-col gap-6">
      <h2 className="text-heading font-bold text-slate-900 dark:text-slate-100 text-center">
        {t('landing.pricing.section_title', 'Simple, honest pricing')}
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6 max-w-3xl mx-auto w-full">
        <div
          data-testid="tier-trial"
          className={`${CARD_BASE} border-slate-100 dark:border-slate-800`}
        >
          <Clock size={20} className="text-slate-400 dark:text-slate-500" aria-hidden="true" />

          <span className="text-caption font-bold w-fit bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-pill px-2 py-0.5">
            {t('landing.pricing.trial_badge', '14 days')}
          </span>

          <p className="text-heading font-bold text-slate-900 dark:text-slate-100">
            {t('landing.pricing.tier_trial', 'Trial')}
          </p>

          <p className="text-heading font-bold text-slate-900 dark:text-slate-100">
            {t('landing.pricing.trial_headline', '14 days of Pro, free')}
          </p>

          <p className="text-body text-slate-500 dark:text-slate-400">
            {t('landing.pricing.trial_no_card', 'No card required')}
          </p>

          <p className="text-body text-slate-500 dark:text-slate-400">
            {t(
              'landing.pricing.after_trial',
              'After 14 days: your data stays, editing becomes read-only until you subscribe.',
            )}
          </p>
        </div>

        <div
          data-testid="tier-pro"
          className={`${CARD_BASE} ring-2 ring-brand-600 dark:ring-brand-400 shadow-glow-brand`}
        >
          <Zap size={20} className="text-brand-600 dark:text-brand-400" aria-hidden="true" />

          <span className="text-caption font-bold text-brand-700 dark:text-brand-300 uppercase tracking-wide w-fit">
            {t('landing.pricing.pro_recommended', 'Recommended')}
          </span>

          <p className="text-heading font-bold text-slate-900 dark:text-slate-100">
            {t('landing.pricing.tier_pro', 'Pro')}
          </p>

          {/* Price digits come from the same billing.* keys PaywallPage/UpgradePromptModal
              read — a single source of price truth across every surface. Inline fallbacks
              are byte-identical to the en locale value (pinned by plan 38-04). */}
          <p className="text-heading font-bold font-mono text-brand-600 dark:text-brand-400">
            {t('billing.price_monthly', '49 PLN/mo')}
          </p>

          <p className="text-body font-mono text-slate-500 dark:text-slate-400">
            {t('billing.price_annual', '499 PLN/yr')}
          </p>

          <p className="text-body text-green-600 dark:text-green-400">
            {t('landing.pricing.annual_hint', '≈ 1.8 months free')}
          </p>

          <span className="text-caption font-bold w-fit bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 rounded-pill px-2 py-0.5">
            {t('landing.pricing.vat_note', 'Price includes VAT')}
          </span>

          <ul className="flex flex-col gap-2">
            <li className="flex items-center gap-2 text-body text-slate-700 dark:text-slate-300">
              <CheckCircle2
                size={16}
                className="text-green-600 dark:text-green-400 shrink-0"
                aria-hidden="true"
              />
              {t('billing.feature_sync', 'Sync across all devices')}
            </li>
            <li className="flex items-center gap-2 text-body text-slate-700 dark:text-slate-300">
              <CheckCircle2
                size={16}
                className="text-green-600 dark:text-green-400 shrink-0"
                aria-hidden="true"
              />
              {t('billing.feature_analytics', 'Margin analytics')}
            </li>
            <li className="flex items-center gap-2 text-body text-slate-700 dark:text-slate-300">
              <CheckCircle2
                size={16}
                className="text-green-600 dark:text-green-400 shrink-0"
                aria-hidden="true"
              />
              {t('landing.benefit_2', '{{count}}-service catalog with price sync — no manual entry', {
                count: SERVICES_DB.length,
              })}
            </li>
          </ul>

          <a
            href="#landing-auth-form"
            className="text-caption font-bold text-brand-700 dark:text-brand-300"
          >
            {t('landing.pricing.pro_scroll_hint', '↑ Start your trial above')}
          </a>
        </div>

        {/* Team is announced only: no Stripe price, no seat model, no gating exists in
            code (src/stores/useAuthStore.ts:31, plan: 'free' | 'pro'). This card renders
            zero button/a/tabindex/role="button" nodes — a fail-first test proves it, not
            an absence anyone has to infer. No feature list: the tier's functionality has
            not been designed, so enumerating it would be invention. */}
        <div
          data-testid="tier-team"
          className={`${CARD_BASE} border-slate-200 dark:border-slate-700`}
        >
          <Users size={20} className="text-slate-400 dark:text-slate-500" aria-hidden="true" />

          <p className="text-heading font-bold text-slate-900 dark:text-slate-100">
            {t('landing.pricing.tier_team', 'Team')}
          </p>

          <p className="text-heading font-bold font-mono text-slate-500 dark:text-slate-400">
            {t('landing.pricing.team_price', '99 PLN/mo')}
          </p>

          <span className="text-caption font-bold w-fit bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-pill px-2 py-0.5">
            {t('landing.pricing.team_soon', 'Coming soon')}
          </span>

          <p className="text-body text-slate-500 dark:text-slate-400">
            {t('landing.pricing.team_positioning', 'For crews working together')}
          </p>
        </div>
      </div>
    </section>
  );
};
